import apiClient from './client'
import type {
  Item,
  ItemWithFeed,
  ItemFilters,
  ApiResponse,
  PaginatedResponse,
} from '@/types/models'
import {
  adaptListResponse,
  adaptItemResponse,
  adaptItemFilters,
} from '@/lib/utils/api-adapter'

/**
 * Item API endpoints
 */
export const itemsApi = {
  /**
   * List items with optional filters
   */
  async list(filters?: ItemFilters): Promise<PaginatedResponse<ItemWithFeed>> {
    const response = await apiClient.get<any>('/api/v1/items', {
      params: adaptItemFilters(filters) as Record<string, string | number | boolean | undefined>,
    })
    const adapted = adaptListResponse<any>(response, 'items')
    return {
      ...adapted,
      data: adapted.data.map((item: any) => ({
        ...adaptItemResponse(item),
        feed: item.feed, // Assume feed is included in the response
      })) as ItemWithFeed[],
    }
  },

  /**
   * Get a single item by ID
   */
  async get(id: string): Promise<ApiResponse<ItemWithFeed>> {
    const response = await apiClient.get<any>(`/api/v1/items/${id}`)
    return {
      data: {
        ...adaptItemResponse(response),
        feed: response.feed,
      } as ItemWithFeed,
    }
  },

  /**
   * Mark an item as read
   */
  async markRead(id: string): Promise<ApiResponse<Item>> {
    const response = await apiClient.post<any>(`/api/v1/items/${id}/read`)
    return {
      data: adaptItemResponse(response),
    }
  },

  /**
   * Mark an item as unread
   */
  async markUnread(id: string): Promise<ApiResponse<Item>> {
    const response = await apiClient.post<any>(`/api/v1/items/${id}/unread`)
    return {
      data: adaptItemResponse(response),
    }
  },

  /**
   * Mark multiple items as read
   */
  async markMultipleRead(itemIds: string[]): Promise<ApiResponse<{ count: number }>> {
    return apiClient.post<ApiResponse<{ count: number }>>('/api/v1/items/bulk/read', {
      itemIds,
    })
  },

  /**
   * Toggle favorite status
   */
  async toggleFavorite(id: string): Promise<ApiResponse<Item>> {
    const response = await apiClient.post<any>(`/api/v1/items/${id}/favorite`)
    return {
      data: adaptItemResponse(response),
    }
  },

  /**
   * Delete an item
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/v1/items/${id}`)
  },

  /**
   * Mark all items as read for a feed
   */
  async markAllRead(feedId?: string): Promise<ApiResponse<{ count: number }>> {
    const params = feedId ? { feed_id: feedId } : undefined
    return apiClient.post<ApiResponse<{ count: number }>>(
      '/api/v1/items/mark-all-read',
      {},
      { params }
    )
  },
}

export default itemsApi
